import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  FaQrcode, FaChartBar, FaUsers,
  FaExclamationTriangle, FaCheckCircle, FaEye,
  FaTimes, FaStar, FaStarHalfAlt, FaRegStar,
  FaDownload, FaArrowUp, FaLink,
  FaGraduationCap, FaCalendarAlt, FaMapMarkerAlt,
  FaChevronDown, FaInfoCircle, FaLock,
  FaCheck, FaPlus, FaClock, FaStopCircle
} from 'react-icons/fa';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Cell
} from 'recharts';
import Sidebar from '../components/Sidebar';
import Navbar  from '../components/Navbar';
import { useTheme } from '../context/ThemeContext';
import Qrcodepanel       from '../components/Qrcodepanel';
import { evalApi } from '../hooks/useEvalApi';
import CreateSessionModalAvis from '../components/CreateSessionModalAvis';

// ── CONSTANTS LAYOUT ──────────────────────────────────────────────────────────
const SB_OPEN   = 252;
const SB_CLOSED =  74;
const SB_GAP    =  12;
const NB_HEIGHT =  68;
const NB_TOP    =  12;

// ── HELPERS ───────────────────────────────────────────────────────────────────
const avg = arr => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

const calcCategoryAvg = (avis, category) => {
  const vals = avis.flatMap(a => Object.values(a[category] || {}));
  return avg(vals);
};

const noteColor = n => {
  if (n >= 4.5) return '#198754';
  if (n >= 3.5) return '#20c997';
  if (n >= 2.5) return '#fd7e14';
  return '#dc3545';
};

const formatCountdown = (secs) => {
  if (secs === null || secs === undefined) return null;
  if (secs <= 0) return '00:00';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const StarRating = ({ value, size = 13 }) => {
  const full  = Math.floor(value);
  const half  = value - full >= 0.3 && value - full < 0.8;
  const empty = 5 - full - (half ? 1 : 0);
  return (
    <span style={{ color: '#fd7e14', display: 'inline-flex', gap: 1 }}>
      {[...Array(full)].map((_, i)  => <FaStar        key={`f${i}`} style={{ fontSize: size }} />)}
      {half                          && <FaStarHalfAlt key="h"       style={{ fontSize: size }} />}
      {[...Array(empty)].map((_, i) => <FaRegStar      key={`e${i}`} style={{ fontSize: size }} />)}
    </span>
  );
};

const NoteBar = ({ value, max = 5, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'rgba(0,0,0,0.08)', overflow: 'hidden' }}>
      <div style={{ width: `${(value / max) * 100}%`, height: '100%', borderRadius: 3, background: color || noteColor(value), transition: 'width 0.6s ease' }} />
    </div>
    <span style={{ fontSize: 12, fontWeight: 700, color: color || noteColor(value), minWidth: 28 }}>{value.toFixed(1)}</span>
  </div>
);

// ── CRITÈRES ──────────────────────────────────────────────────────────────────
const CRITERES = {
  formation: [
    { key: 'contenuObjectifs', label: 'Contenu conforme aux objectifs'  },
    { key: 'theoriePratique',  label: 'Rapport théorie / pratique'      },
    { key: 'duree',            label: 'Durée'                           },
    { key: 'rythme',           label: 'Rythme'                          },
    { key: 'support',          label: 'Support pédagogique'             },
    { key: 'adequation',       label: 'Adéquation avec les objectifs'   },
    { key: 'apport',           label: 'Apport immédiat pour le poste'   },
    { key: 'transfert',        label: 'Transférabilité professionnelle' },
  ],
  formateur: [
    { key: 'clarte',        label: 'Clarté du cours'     },
    { key: 'maitrise',      label: 'Maîtrise du sujet'   },
    { key: 'disponibilite', label: 'Disponibilité'        },
    { key: 'methode',       label: 'Méthode pédagogique' },
  ],
  impact: [
    { key: 'utilite',       label: 'Utilité en situation de travail'          },
    { key: 'developpement', label: 'Amélioration développement professionnel' },
  ],
  logistique: [
    { key: 'salle',        label: 'Salle de formation'            },
    { key: 'materiel',     label: 'Matériel & qualité projection' },
    { key: 'restauration', label: 'Qualité de la restauration'    },
  ],
};

const CATEGORIES_CFG = [
  { key: 'formation',  label: 'La Formation',  color: '#198754', icon: <FaGraduationCap /> },
  { key: 'formateur',  label: 'Le Formateur',  color: '#fd7e14', icon: <FaUsers />         },
  { key: 'impact',     label: "L'Impact",      color: '#20c997', icon: <FaArrowUp />       },
  { key: 'logistique', label: 'La Logistique', color: '#0d6efd', icon: <FaMapMarkerAlt />  },
];

// ── COMPOSANT PRINCIPAL ───────────────────────────────────────────────────────
const AvisAChaud = () => {
  const [sidebarOpen,      setSidebarOpen]      = useState(true);
  const [sessions,         setSessions]         = useState([]);
  const [selectedId,       setSelectedId]       = useState(null);
  const [sessionDetail,    setSessionDetail]    = useState(null);
  const [loading,          setLoading]          = useState(true);
  const [loadingDetail,    setLoadingDetail]    = useState(false);
  const [activeTab,        setActiveTab]        = useState('synthese');
  const [detailAvis,       setDetailAvis]       = useState(null);
  const [showDropdown,     setShowDropdown]     = useState(false);
  const [showQRPanel,      setShowQRPanel]      = useState(false);
  const [showCreateModal,  setShowCreateModal]  = useState(false);
  const [countdown,        setCountdown]        = useState(null); // secondes restantes
  const [countdownStatus,  setCountdownStatus]  = useState(null); // statut de la session

  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const c = isDark ? {
    bg: '#090c16', card: 'rgba(255,255,255,0.04)', cardB: 'rgba(255,255,255,0.08)',
    text: '#c8d4e0', textDim: '#5a6b82', title: '#eef2f7',
    shadow: '0 4px 24px rgba(0,0,0,0.4)', input: 'rgba(255,255,255,0.06)', inputB: 'rgba(255,255,255,0.12)',
    surface: 'rgba(255,255,255,0.04)', divider: 'rgba(255,255,255,0.07)',
    theadBg: 'rgba(255,255,255,0.07)', theadText: 'rgba(255,255,255,0.45)',
    rowHover: 'rgba(255,255,255,0.03)', rowBorder: 'rgba(255,255,255,0.06)',
    modalBg: '#0f172a', overlay: 'rgba(9,12,22,0.8)', dropBg: '#111827', gridLine: 'rgba(255,255,255,0.06)',
  } : {
    bg: '#f0f3f8', card: '#ffffff', cardB: 'rgba(0,0,0,0.07)',
    text: '#4d6070', textDim: '#8fa3b5', title: '#1a2535',
    shadow: '0 2px 12px rgba(0,0,0,0.05)', input: '#f7f9fb', inputB: 'rgba(0,0,0,0.08)',
    surface: 'rgba(0,0,0,0.03)', divider: 'rgba(0,0,0,0.07)',
    theadBg: '#1a2535', theadText: 'rgba(255,255,255,0.5)',
    rowHover: '#fafbfc', rowBorder: 'rgba(0,0,0,0.055)',
    modalBg: '#ffffff', overlay: 'rgba(15,23,42,0.6)', dropBg: '#ffffff', gridLine: 'rgba(0,0,0,0.06)',
  };

  // ── Chargement initial des sessions ────────────────────────────────────────
  useEffect(() => {
    evalApi.getSessions()
      .then(res => {
        setSessions(res.data);
        if (res.data.length > 0) setSelectedId(res.data[0].id);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // ── Chargement du détail quand on change de session ────────────────────────
  useEffect(() => {
    if (!selectedId) return;
    setLoadingDetail(true);
    evalApi.getSession(selectedId)
      .then(res => {
        setSessionDetail(res.data);
        setCountdown(res.data.secondes_restantes);
        setCountdownStatus(res.data.statut);
      })
      .catch(console.error)
      .finally(() => setLoadingDetail(false));
  }, [selectedId]);

  // ── Polling compte à rebours (toutes les 10s) ──────────────────────────────
  useEffect(() => {
    if (!selectedId) return;
    const interval = setInterval(async () => {
      try {
        const res = await evalApi.getCountdown(selectedId);
        const d   = res.data;
        setCountdown(d.secondes_restantes);
        setCountdownStatus(d.statut);
        // Si changement de statut ou nouveau avis → recharger le détail
        if (d.statut === 'termine' || d.reponses_count !== (sessionDetail?.avis?.length ?? 0)) {
          const detRes = await evalApi.getSession(selectedId);
          setSessionDetail(detRes.data);
          setSessions(prev => prev.map(s => s.id === selectedId
            ? { ...s, reponses_count: d.reponses_count, statut: d.statut, qr_actif: d.qr_actif }
            : s
          ));
        }
      } catch { /* silencieux */ }
    }, 10_000);
    return () => clearInterval(interval);
  }, [selectedId, sessionDetail]);

  // ── Données dérivées ───────────────────────────────────────────────────────
  const formation   = sessions.find(s => s.id === selectedId) || null;
  const avis        = sessionDetail?.avis || [];
  const nbAttendu   = formation?.participants_attendus || 0;
  const nbRecu      = avis.length;
  const anomalie    = nbRecu > nbAttendu;

  const moyennes = useMemo(() => ({
    formation:  calcCategoryAvg(avis, 'formation'),
    formateur:  calcCategoryAvg(avis, 'formateur'),
    impact:     calcCategoryAvg(avis, 'impact'),
    logistique: calcCategoryAvg(avis, 'logistique'),
  }), [avis]);

  const noteMoyenneGlobale = useMemo(() => avg(Object.values(moyennes)), [moyennes]);

  const radarData = [
    { sujet: 'Formation',  note: +moyennes.formation.toFixed(2),  fullMark: 5 },
    { sujet: 'Formateur',  note: +moyennes.formateur.toFixed(2),  fullMark: 5 },
    { sujet: 'Impact',     note: +moyennes.impact.toFixed(2),     fullMark: 5 },
    { sujet: 'Logistique', note: +moyennes.logistique.toFixed(2), fullMark: 5 },
  ];

  const distributionData = useMemo(() => {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    avis.forEach(a => {
      ['formation', 'formateur', 'impact', 'logistique'].forEach(cat => {
        Object.values(a[cat] || {}).forEach(n => { if (counts[n] !== undefined) counts[n]++; });
      });
    });
    return Object.entries(counts).map(([n, v]) => ({ note: `Note ${n}`, count: v, fill: noteColor(+n) }));
  }, [avis]);

  const criteresMoyennes = useMemo(() => {
    const result = {};
    Object.entries(CRITERES).forEach(([cat, crit]) => {
      crit.forEach(({ key }) => {
        const vals = avis.map(a => (a[cat] || {})[key]).filter(Boolean);
        result[`${cat}.${key}`] = avg(vals);
      });
    });
    return result;
  }, [avis]);

  // ── Callbacks ─────────────────────────────────────────────────────────────
  const handleToggleQR = useCallback((actif) => {
    setSessions(prev => prev.map(s => s.id === selectedId ? { ...s, qr_actif: actif } : s));
    setSessionDetail(prev => prev ? { ...prev, qr_actif: actif } : prev);
  }, [selectedId]);

  const handleSessionCreated = useCallback((newSession) => {
    setSessions(prev => [newSession, ...prev]);
    setSelectedId(newSession.id);
  }, []);

  const handleTerminer = useCallback(async () => {
    if (!selectedId || !window.confirm('Clôturer cette session définitivement ?')) return;
    try {
      await evalApi.terminer(selectedId);
      setSessions(prev => prev.map(s => s.id === selectedId ? { ...s, statut: 'termine', qr_actif: false } : s));
      setCountdownStatus('termine');
      setCountdown(0);
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la clôture.');
    }
  }, [selectedId]);

  const handleExportPDF = useCallback(() => {
    if (!formation || avis.length === 0) {
      alert('Aucune donnée à exporter pour cette session.');
      return;
    }

    const LOGO_SRC = '../assets/images/Logo_CINEF.png';
    const logoTag = `<img src="${LOGO_SRC}" alt="CINEF" style="height:60px;object-fit:contain;display:block;margin-bottom:8px" onerror="this.style.display='none';document.getElementById('fb').style.display='block'"/>
      <div id="fb" style="display:none;font-family:Georgia,serif;font-size:30px;font-weight:900;color:#198754;letter-spacing:-1px;margin-bottom:8px">CINEF</div>`;

    const tauxParticipation = nbAttendu > 0 ? Math.round((nbRecu / nbAttendu) * 100) : 0;
    const sessionTerminee = countdownStatus === 'termine';
    const tempsRestant = formatCountdown(countdown);
    
    // Trouver meilleure catégorie et à améliorer
    const categoriesTriees = Object.entries(moyennes).sort((a, b) => b[1] - a[1]);
    const meilleureCat = categoriesTriees[0];
    const aAmeliorerCat = categoriesTriees[categoriesTriees.length - 1];

    // Générer les lignes de critères par catégorie
    const criteresHTML = CATEGORIES_CFG.map(cat => {
      const criteres = CRITERES[cat.key] || [];
      const lignesCriteres = criteres.map(({ key, label }) => {
        const moy = criteresMoyennes[`${cat.key}.${key}`] || 0;
        const barWidth = (moy / 5) * 100;
        return `
          <tr>
            <td style="padding:10px 14px;border-bottom:1px solid #f1f5f9;font-size:13px;color:#4b5563">${label}</td>
            <td style="padding:10px 14px;border-bottom:1px solid #f1f5f9;text-align:center">
              <div style="display:inline-flex;align-items:center;gap:8px;width:200px">
                <div style="flex:1;height:8px;background:#f1f5f9;border-radius:4px;overflow:hidden">
                  <div style="width:${barWidth}%;height:100%;background:${noteColor(moy)};border-radius:4px"></div>
                </div>
                <span style="font-weight:700;color:${noteColor(moy)};min-width:35px;font-size:13px">${moy.toFixed(2)}</span>
              </div>
            </td>
          </tr>
        `;
      }).join('');

      return `
        <div style="margin-bottom:28px;page-break-inside:avoid">
          <div style="display:flex;align-items:center;gap:10px;margin-bottom:14px;padding-bottom:10px;border-bottom:2px solid ${cat.color}">
            <div style="width:36px;height:36px;border-radius:10px;background:${cat.color}15;color:${cat.color};display:flex;align-items:center;justify-content:center;font-size:16px">📊</div>
            <h3 style="margin:0;font-size:16px;font-weight:800;color:#1f2937">${cat.label}</h3>
            <span style="margin-left:auto;font-size:20px;font-weight:900;color:${noteColor(moyennes[cat.key])}">${moyennes[cat.key].toFixed(2)}/5</span>
          </div>
          <table style="width:100%;border-collapse:collapse">
            ${lignesCriteres}
          </table>
        </div>
      `;
    }).join('');

    // Générer le tableau de distribution des notes
    const distributionHTML = distributionData.map(d => {
      const total = distributionData.reduce((sum, item) => sum + item.count, 0);
      const pourcentage = total > 0 ? Math.round((d.count / total) * 100) : 0;
      return `
        <tr>
          <td style="padding:12px 14px;border-bottom:1px solid #f1f5f9;font-weight:600;color:#1f2937">${d.note}</td>
          <td style="padding:12px 14px;border-bottom:1px solid #f1f5f9;text-align:center;font-size:18px;font-weight:800;color:${d.fill}">${d.count}</td>
          <td style="padding:12px 14px;border-bottom:1px solid #f1f5f9">
            <div style="display:flex;align-items:center;gap:10px">
              <div style="flex:1;height:10px;background:#f1f5f9;border-radius:5px;overflow:hidden">
                <div style="width:${pourcentage}%;height:100%;background:${d.fill};border-radius:5px"></div>
              </div>
              <span style="font-weight:700;color:${d.fill};min-width:40px;text-align:right">${pourcentage}%</span>
            </div>
          </td>
        </tr>
      `;
    }).join('');

    const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8"/>
  <title>Rapport d'Évaluation - ${formation.titre}</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'Helvetica Neue',Arial,sans-serif; color:#374151; background:#fff; font-size:13px; line-height:1.5; }
    .page { max-width:900px; margin:0 auto; padding:40px; }
    .header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:32px; padding-bottom:24px; border-bottom:2px solid #198754; }
    .badge { display:inline-block; font-size:11px; font-weight:900; letter-spacing:2px; background:#198754; color:#fff; padding:8px 18px; border-radius:6px; margin-bottom:12px; }
    .meta-grid { display:grid; grid-template-columns:auto 1fr; gap:6px 16px; font-size:12px; }
    .meta-lbl { font-weight:700; color:#6b7280; }
    .meta-val { color:#1f2937; }
    .stats-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; margin-bottom:32px; }
    .stat-card { background:#f9fafb; border:1px solid #e5e7eb; border-radius:12px; padding:18px; text-align:center; }
    .stat-label { font-size:11px; font-weight:800; text-transform:uppercase; letter-spacing:0.5px; color:#9ca3af; margin-bottom:8px; }
    .stat-value { font-size:32px; font-weight:900; color:#1f2937; line-height:1; margin-bottom:4px; }
    .stat-sub { font-size:12px; color:#6b7280; }
    .section { margin-bottom:32px; page-break-inside:avoid; }
    .section-title { font-size:18px; font-weight:800; color:#1f2937; margin-bottom:16px; padding-bottom:10px; border-bottom:2px solid #e5e7eb; }
    .categories-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; margin-bottom:32px; }
    .cat-card { background:#fff; border:2px solid #e5e7eb; border-radius:12px; padding:16px; text-align:center; transition:all 0.2s; }
    .cat-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:0.5px; color:#6b7280; margin-bottom:8px; }
    .cat-note { font-size:28px; font-weight:900; margin-bottom:6px; }
    .footer { text-align:center; font-size:11px; color:#9ca3af; padding-top:24px; border-top:1px solid #e5e7eb; margin-top:40px; }
    @media print {
      @page { margin:12mm; }
      body { -webkit-print-color-adjust:exact; print-color-adjust:exact; }
      .page { padding:0; }
    }
  </style>
</head>
<body>
  <div class="page">
    <!-- En-tête -->
    <div class="header">
      <div>
        ${logoTag}
        <p style="font-size:12px;color:#6b7280;margin-bottom:2px">Centre International de Formation et d'Expertise</p>
        <p style="font-size:11px;color:#9ca3af">info@cinef-bj.com · +229 63 18 60 60</p>
      </div>
      <div style="text-align:right">
        <div class="badge">RAPPORT D'ÉVALUATION</div>
        <div class="meta-grid" style="text-align:left">
          <span class="meta-lbl">Formation:</span><span class="meta-val" style="font-weight:700">${formation.titre}</span>
          <span class="meta-lbl">Dates:</span><span class="meta-val">${formation.dates}</span>
          <span class="meta-lbl">Lieu:</span><span class="meta-val">${formation.lieu}</span>
          <span class="meta-lbl">Date export:</span><span class="meta-val">${new Date().toLocaleDateString('fr-FR')}</span>
        </div>
      </div>
    </div>

    <!-- Titre principal -->
    <div style="text-align:center;margin-bottom:32px">
      <h1 style="font-size:28px;font-weight:900;color:#1f2937;margin-bottom:8px">Évaluation à Chaud</h1>
      <p style="font-size:14px;color:#6b7280">Résultats des questionnaires de satisfaction par session</p>
    </div>

    <!-- Statistiques principales -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-label">Avis reçus</div>
        <div class="stat-value" style="color:#198754">${nbRecu}</div>
        <div class="stat-sub">sur ${nbAttendu} attendus</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Taux participation</div>
        <div class="stat-value" style="color:#0d6efd">${tauxParticipation}%</div>
        <div class="stat-sub">${nbRecu}/${nbAttendu} participants</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Note globale</div>
        <div class="stat-value" style="color:${noteColor(noteMoyenneGlobale)}">${noteMoyenneGlobale.toFixed(2)}</div>
        <div class="stat-sub">sur 5.00</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Statut session</div>
        <div class="stat-value" style="color:${sessionTerminee ? '#6b7280' : '#198754'};font-size:14px">${sessionTerminee ? 'TERMINÉE' : 'ACTIVE'}</div>
        <div class="stat-sub">${sessionTerminee ? 'Clôturée' : tempsRestant || 'En cours'}</div>
      </div>
    </div>

    <!-- Moyennes par catégorie -->
    <div class="section">
      <h2 class="section-title">Moyennes par Catégorie</h2>
      <div class="categories-grid">
        ${CATEGORIES_CFG.map(cat => `
          <div class="cat-card" style="border-color:${cat.color}">
            <div class="cat-label" style="color:${cat.color}">${cat.label}</div>
            <div class="cat-note" style="color:${noteColor(moyennes[cat.key])}">${moyennes[cat.key].toFixed(2)}<span style="font-size:16px;color:#9ca3af">/5</span></div>
            <div style="color:#6b7280;font-size:12px;font-weight:600">${moyennes[cat.key] >= 4.5 ? 'Excellent' : moyennes[cat.key] >= 3.5 ? 'Très bien' : moyennes[cat.key] >= 2.5 ? 'Bien' : 'À améliorer'}</div>
          </div>
        `).join('')}
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:20px">
        <div style="background:#f0fdf4;border:1px solid #86efac;border-radius:12px;padding:16px">
          <div style="font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:0.5px;color:#16a34a;margin-bottom:6px">✓ Point fort</div>
          <div style="font-size:16px;font-weight:700;color:#1f2937">${CATEGORIES_CFG.find(c => c.key === meilleureCat[0])?.label}</div>
          <div style="font-size:20px;font-weight:900;color:#16a34a;margin-top:4px">${meilleureCat[1].toFixed(2)}/5</div>
        </div>
        <div style="background:#fef3c7;border:1px solid #fbbf24;border-radius:12px;padding:16px">
          <div style="font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:0.5px;color:#d97706;margin-bottom:6px">⚠ À améliorer</div>
          <div style="font-size:16px;font-weight:700;color:#1f2937">${CATEGORIES_CFG.find(c => c.key === aAmeliorerCat[0])?.label}</div>
          <div style="font-size:20px;font-weight:900;color:#d97706;margin-top:4px">${aAmeliorerCat[1].toFixed(2)}/5</div>
        </div>
      </div>
    </div>

    <!-- Distribution des notes -->
    <div class="section">
      <h2 class="section-title">Distribution des Notes</h2>
      <table style="width:100%;border-collapse:collapse;background:#fff;border:1px solid #e5e7eb;border-radius:12px;overflow:hidden">
        <thead>
          <tr style="background:#1f2937">
            <th style="padding:12px 14px;text-align:left;font-size:12px;font-weight:800;color:#fff;text-transform:uppercase;letter-spacing:0.5px">Note</th>
            <th style="padding:12px 14px;text-align:center;font-size:12px;font-weight:800;color:#fff;text-transform:uppercase;letter-spacing:0.5px">Nombre</th>
            <th style="padding:12px 14px;text-align:left;font-size:12px;font-weight:800;color:#fff;text-transform:uppercase;letter-spacing:0.5px">Répartition</th>
          </tr>
        </thead>
        <tbody>
          ${distributionHTML}
        </tbody>
      </table>
    </div>

    <!-- Détail par critère -->
    <div class="section">
      <h2 class="section-title">Détail par Critère</h2>
      ${criteresHTML}
    </div>

    <!-- Pied de page -->
    <div class="footer">
      <p>CINEF — Centre International de Formation et d'Expertise</p>
      <p style="margin-top:6px">Ce rapport a été généré automatiquement le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</p>
      <p style="margin-top:6px">Rapport confidentiel • ${nbRecu} avis analysés</p>
    </div>
  </div>
  <script>
    window.onload = () => {
      window.focus();
      window.print();
    };
  </script>
</body>
</html>`;

    const w = window.open('', '_blank');
    if (!w) {
      alert('Veuillez autoriser les popups pour exporter le PDF.');
      return;
    }
    w.document.write(html);
    w.document.close();
  }, [formation, avis, nbRecu, nbAttendu, moyennes, noteMoyenneGlobale, distributionData, criteresMoyennes, countdownStatus, countdown]);

  const mainPaddingLeft = `${SB_GAP + (sidebarOpen ? SB_OPEN : SB_CLOSED) + 12}px`;
  const mainPaddingTop  = `${NB_TOP + NB_HEIGHT + 20}px`;

  const countdownDisplay  = formatCountdown(countdown);
  const isSessionActive   = countdownStatus === 'actif';
  const isSessionTerminee = countdownStatus === 'termine';

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: c.dropBg, border: `1px solid ${c.cardB}`, borderRadius: 10, padding: '10px 14px', fontSize: 13, color: c.title, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
        <div style={{ fontWeight: 700 }}>{payload[0].payload.note}</div>
        <div style={{ color: c.textDim }}>{payload[0].value} réponse{payload[0].value > 1 ? 's' : ''}</div>
      </div>
    );
  };

  return (
    <div style={{ background: c.bg, minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; font-family:'Plus Jakarta Sans',sans-serif; }
        .av-main { transition:padding-left 0.28s cubic-bezier(0.4,0,0.2,1); min-height:100vh; width:100%; }
        .av-body { padding:22px 24px 48px; }
        .av-tabs { display:flex; gap:4px; border-radius:12px; padding:4px; width:fit-content; margin-bottom:22px; }
        .av-tab  { display:flex; align-items:center; gap:8px; padding:9px 20px; border-radius:9px; border:none; font-size:13.5px; font-weight:600; cursor:pointer; transition:all 0.2s; }
        .av-form-selector { position:relative; }
        .av-form-btn { display:flex; align-items:center; gap:12px; padding:14px 18px; border-radius:13px; border:1px solid; cursor:pointer; transition:all 0.2s; background:transparent; width:100%; text-align:left; }
        .av-form-btn:hover { border-color:#198754 !important; }
        .av-dropdown { position:absolute; top:calc(100% + 8px); left:0; right:0; border-radius:13px; border:1px solid; box-shadow:0 12px 36px rgba(0,0,0,0.15); z-index:100; overflow:hidden; animation:dropIn 0.18s ease; }
        @keyframes dropIn { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:none} }
        .av-drop-item { padding:14px 18px; cursor:pointer; transition:background 0.15s; border-bottom:1px solid; }
        .av-drop-item:last-child { border-bottom:none; }
        .av-drop-item:hover { background:rgba(25,135,84,0.06) !important; }
        .av-card { border-radius:14px; box-shadow:${c.shadow}; }
        .av-score-ring { width:110px; height:110px; border-radius:50%; display:flex; align-items:center; justify-content:center; flex-direction:column; position:relative; flex-shrink:0; }
        .av-tbl { width:100%; border-collapse:collapse; }
        .av-tbl th { padding:11px 16px; font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:0.7px; text-align:left; }
        .av-tbl td { padding:12px 16px; border-bottom:1px solid; vertical-align:middle; }
        .av-tbl tr:last-child td { border-bottom:none; }
        .av-trow:hover td { background:${c.rowHover}; }
        .av-npill { display:inline-flex; align-items:center; justify-content:center; width:28px; height:28px; border-radius:7px; font-size:12px; font-weight:700; flex-shrink:0; }
        .av-overlay { position:fixed; inset:0; backdrop-filter:blur(6px); display:flex; align-items:center; justify-content:center; z-index:2000; padding:20px; animation:fadeIn 0.2s ease; }
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        .av-modal { border-radius:20px; width:100%; max-width:740px; max-height:92vh; display:flex; flex-direction:column; box-shadow:0 24px 64px rgba(0,0,0,0.22); overflow:hidden; animation:popIn 0.25s cubic-bezier(0.34,1.56,0.64,1); }
        @keyframes popIn { from{opacity:0;transform:scale(0.94) translateY(16px)} to{opacity:1;transform:none} }
        .av-alert { display:flex; align-items:flex-start; gap:12px; padding:14px 18px; border-radius:12px; border:1px solid; margin-bottom:18px; }
        .av-countdown { display:flex; align-items:center; gap:8px; padding:10px 18px; border-radius:11px; font-size:14px; font-weight:800; border:1.5px solid; }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.6} }
        @media (max-width:1024px) { .av-main { padding-left:0 !important; } }
        @media (max-width:768px)  { .av-body { padding:14px 14px 32px; } .av-tabs { width:100%; } .av-tab { flex:1; justify-content:center; } }
      `}</style>

      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <Navbar  sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} pageTitle="Avis à Chaud" adminName="Jean Dupont" />

      <main className="av-main" style={{ paddingLeft: mainPaddingLeft, paddingTop: mainPaddingTop }}>
        <div className="av-body">

          {/* ── En-tête ── */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 22, gap: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 48, height: 48, borderRadius: 13, background: '#198754', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                <FaChartBar />
              </div>
              <div>
                <h1 style={{ fontSize: 20, fontWeight: 700, color: c.title, marginBottom: 2 }}>Évaluations à Chaud</h1>
                <p style={{ fontSize: 13, color: c.textDim }}>Résultats des questionnaires de satisfaction par session</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>

              {/* ── Compte à rebours ── */}
              {isSessionActive && countdownDisplay !== null && (
                <div
                  className="av-countdown"
                  style={{
                    background: countdown > 300 ? 'rgba(25,135,84,0.08)' : 'rgba(220,53,69,0.08)',
                    borderColor: countdown > 300 ? 'rgba(25,135,84,0.3)' : 'rgba(220,53,69,0.3)',
                    color: countdown > 300 ? '#198754' : '#dc3545',
                    animation: countdown <= 300 ? 'pulse 1.5s infinite' : 'none',
                  }}
                >
                  <FaClock />
                  {countdown > 0 ? countdownDisplay : 'Expiré'}
                </div>
              )}
              {isSessionTerminee && (
                <div className="av-countdown" style={{ background: 'rgba(108,117,125,0.08)', borderColor: 'rgba(108,117,125,0.25)', color: '#6c757d' }}>
                  <FaCheckCircle /> Session terminée
                </div>
              )}

              {/* ── Bouton créer session ── */}
              <button
                onClick={() => setShowCreateModal(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 18px',
                  background: 'linear-gradient(135deg,#fd7e14,#e65c00)',
                  color: '#fff', border: 'none',
                  borderRadius: 11, fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(253,126,20,0.28)',
                }}
              >
                <FaPlus /> Nouvelle session
              </button>

              {/* ── Bouton QR ── */}
              {formation && (
                <button
                  onClick={() => setShowQRPanel(true)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 18px',
                    background: formation?.qr_actif ? 'rgba(25,135,84,0.1)' : c.surface,
                    color:      formation?.qr_actif ? '#198754' : c.textDim,
                    border: `1.5px solid ${formation?.qr_actif ? 'rgba(25,135,84,0.3)' : c.cardB}`,
                    borderRadius: 11, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
                  }}
                >
                  <FaQrcode /> {formation?.qr_actif ? 'QR Actif' : 'Gérer QR Code'}
                </button>
              )}

              {/* ── Bouton clôturer ── */}
              {isSessionActive && (
                <button
                  onClick={handleTerminer}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 18px',
                    background: 'rgba(220,53,69,0.08)', color: '#dc3545',
                    border: '1.5px solid rgba(220,53,69,0.25)',
                    borderRadius: 11, fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  <FaStopCircle /> Clôturer
                </button>
              )}

              <button onClick={handleExportPDF} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', background: 'transparent', color: '#198754', border: '1.5px solid rgba(25,135,84,0.3)', borderRadius: 11, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>
                <FaDownload /> Exporter PDF
              </button>
            </div>
          </div>

          {/* ── État de chargement ── */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: c.textDim, fontSize: 14 }}>
              Chargement des sessions…
            </div>
          )}

          {/* ── Aucune session ── */}
          {!loading && sessions.length === 0 && (
            <div className="av-card" style={{ padding: '60px 20px', textAlign: 'center', background: c.card, border: `1px solid ${c.cardB}` }}>
              <FaQrcode style={{ fontSize: 48, color: c.textDim, display: 'block', margin: '0 auto 16px', opacity: 0.3 }} />
              <div style={{ fontSize: 16, fontWeight: 700, color: c.title, marginBottom: 8 }}>Aucune session d'évaluation</div>
              <div style={{ fontSize: 13, color: c.textDim, marginBottom: 20 }}>Créez votre première session pour générer un QR code.</div>
              <button
                onClick={() => setShowCreateModal(true)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 24px', background: '#fd7e14', color: '#fff', border: 'none', borderRadius: 11, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
              >
                <FaPlus /> Créer une session
              </button>
            </div>
          )}

          {!loading && sessions.length > 0 && (
            <>
              {/* ── Sélecteur de session ── */}
              <div className="av-form-selector" style={{ marginBottom: 20 }}>
                <div className="av-form-btn av-card" style={{ background: c.card, border: `1px solid ${c.cardB}` }} onClick={() => setShowDropdown(v => !v)}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(25,135,84,0.1)', color: '#198754', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <FaGraduationCap />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: c.textDim, marginBottom: 2 }}>Session sélectionnée</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: c.title, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formation?.titre}</div>
                    <div style={{ fontSize: 12, color: c.textDim, marginTop: 2, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                      <span><FaCalendarAlt style={{ marginRight: 5, fontSize: 10 }} />{formation?.dates}</span>
                      <span><FaMapMarkerAlt style={{ marginRight: 5, fontSize: 10 }} />{formation?.lieu}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    {/* Badge statut */}
                    {formation?.statut === 'termine' && (
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: '#6c757d', background: 'rgba(108,117,125,0.1)', padding: '3px 10px', borderRadius: 20 }}>Terminée</span>
                    )}
                    {formation?.qr_actif && formation?.statut !== 'termine' && (
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: '#198754', background: 'rgba(25,135,84,0.1)', padding: '3px 10px', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 5 }}>
                        <FaQrcode style={{ fontSize: 10 }} /> QR Actif
                      </span>
                    )}
                    {!formation?.qr_actif && formation?.statut !== 'termine' && (
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: c.textDim, background: c.surface, padding: '3px 10px', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 5 }}>
                        <FaLock style={{ fontSize: 9 }} /> QR Fermé
                      </span>
                    )}
                    <FaChevronDown style={{ color: c.textDim, fontSize: 12, transition: 'transform 0.2s', transform: showDropdown ? 'rotate(180deg)' : 'none' }} />
                  </div>
                </div>

                {showDropdown && (
                  <div className="av-dropdown" style={{ background: c.dropBg, borderColor: c.cardB }}>
                    {sessions.map(s => (
                      <div key={s.id} className="av-drop-item"
                        style={{ background: s.id === selectedId ? 'rgba(25,135,84,0.06)' : 'transparent', borderBottomColor: c.divider }}
                        onClick={() => { setSelectedId(s.id); setShowDropdown(false); setActiveTab('synthese'); }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 13.5, fontWeight: 600, color: c.title, marginBottom: 2 }}>{s.titre}</div>
                            <div style={{ fontSize: 12, color: c.textDim }}>
                              {s.dates} · {s.reponses_count ?? 0} avis / {s.participants_attendus} attendus
                              {s.statut === 'termine' && <span style={{ marginLeft: 8, color: '#6c757d', fontWeight: 600 }}>· Terminée</span>}
                            </div>
                          </div>
                          {s.id === selectedId && <FaCheckCircle style={{ color: '#198754', flexShrink: 0 }} />}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Alerte anomalie ── */}
              {anomalie && (
                <div className="av-alert" style={{ background: 'rgba(220,53,69,0.06)', borderColor: 'rgba(220,53,69,0.2)' }}>
                  <FaExclamationTriangle style={{ color: '#dc3545', fontSize: 18, flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#dc3545', marginBottom: 3 }}>Anomalie détectée — avis excédentaires</div>
                    <div style={{ fontSize: 13, color: c.text }}>
                      <strong>{nbRecu} avis</strong> reçus pour <strong>{nbAttendu} participants</strong> attendus.{' '}
                      {nbRecu - nbAttendu} réponse{nbRecu - nbAttendu > 1 ? 's' : ''} supplémentaire{nbRecu - nbAttendu > 1 ? 's' : ''} détectée{nbRecu - nbAttendu > 1 ? 's' : ''}. Veuillez vérifier l'intégrité du formulaire.
                    </div>
                  </div>
                </div>
              )}

              {/* ── Indicateurs rapides ── */}
              {!loadingDetail && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 20 }}>
                  {[
                    { label: 'Avis reçus',         val: `${nbRecu} / ${nbAttendu}`, color: '#198754', sub: `${nbAttendu ? Math.round(nbRecu / nbAttendu * 100) : 0}% de participation` },
                    { label: 'Note globale',        val: avis.length ? noteMoyenneGlobale.toFixed(2) + '/5' : '—', color: noteColor(noteMoyenneGlobale), sub: avis.length ? <StarRating value={noteMoyenneGlobale} /> : 'Aucun avis' },
                    { label: 'Meilleure catégorie', val: avis.length ? Object.entries(moyennes).sort((a, b) => b[1] - a[1])[0]?.[0] : '—', color: '#20c997', sub: avis.length ? `${Object.entries(moyennes).sort((a, b) => b[1] - a[1])[0]?.[1].toFixed(2)}/5` : '' },
                    { label: 'À améliorer',         val: avis.length ? Object.entries(moyennes).sort((a, b) => a[1] - b[1])[0]?.[0] : '—', color: '#fd7e14', sub: avis.length ? `${Object.entries(moyennes).sort((a, b) => a[1] - b[1])[0]?.[1].toFixed(2)}/5` : '' },
                  ].map((k, i) => (
                    <div key={i} className="av-card" style={{ padding: '18px 18px', position: 'relative', overflow: 'hidden', background: c.card, border: `1px solid ${c.cardB}` }}>
                      <div style={{ position: 'absolute', right: -14, top: -14, width: 64, height: 64, borderRadius: '50%', background: k.color, opacity: 0.07 }} />
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: c.textDim, marginBottom: 8 }}>{k.label}</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: k.color, marginBottom: 4, textTransform: i >= 2 ? 'capitalize' : undefined }}>{k.val}</div>
                      <div style={{ fontSize: 12, color: c.textDim }}>{k.sub}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Tabs ── */}
              <div className="av-tabs" style={{ background: c.surface, border: `1px solid ${c.cardB}` }}>
                {[
                  { id: 'synthese', label: 'Synthèse globale',  icon: <FaChartBar /> },
                  { id: 'detail',   label: 'Avis par personne', icon: <FaUsers />    },
                ].map(tab => (
                  <button key={tab.id} className="av-tab"
                    style={{ background: activeTab === tab.id ? '#198754' : 'transparent', color: activeTab === tab.id ? '#fff' : c.textDim }}
                    onClick={() => setActiveTab(tab.id)}>
                    {tab.icon} {tab.label}
                  </button>
                ))}
              </div>

              {/* ── SYNTHÈSE ── */}
              {activeTab === 'synthese' && avis.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>

                  <div className="av-card" style={{ padding: '22px 24px', background: c.card, border: `1px solid ${c.cardB}` }}>
                    <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: c.textDim, marginBottom: 18, paddingBottom: 12, borderBottom: `1px solid ${c.divider}` }}>Vue d'ensemble par catégorie</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 18 }}>
                      <div className="av-score-ring" style={{ background: `conic-gradient(${noteColor(noteMoyenneGlobale)} ${noteMoyenneGlobale / 5 * 360}deg, ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.07)'} 0deg)` }}>
                        <div style={{ width: 82, height: 82, borderRadius: '50%', background: c.card, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: 26, fontWeight: 800, color: noteColor(noteMoyenneGlobale), lineHeight: 1 }}>{noteMoyenneGlobale.toFixed(1)}</span>
                          <span style={{ fontSize: 11, color: c.textDim, fontWeight: 600 }}>/5</span>
                        </div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: c.title, marginBottom: 4 }}>Score global</div>
                        <StarRating value={noteMoyenneGlobale} size={15} />
                        <div style={{ fontSize: 12, color: c.textDim, marginTop: 6 }}>{nbRecu} participant{nbRecu > 1 ? 's' : ''} évalué{nbRecu > 1 ? 's' : ''}</div>
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={230}>
                      <RadarChart data={radarData}>
                        <PolarGrid stroke={c.gridLine} />
                        <PolarAngleAxis dataKey="sujet" tick={{ fontSize: 12, fill: c.textDim, fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
                        <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fontSize: 10, fill: c.textDim }} tickCount={6} />
                        <Radar name="Note" dataKey="note" stroke="#198754" fill="#198754" fillOpacity={0.18} strokeWidth={2} dot={{ fill: '#198754', r: 4 }} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="av-card" style={{ padding: '22px 24px', background: c.card, border: `1px solid ${c.cardB}` }}>
                    <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: c.textDim, marginBottom: 18, paddingBottom: 12, borderBottom: `1px solid ${c.divider}` }}>Distribution des notes</div>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={distributionData} barSize={38}>
                        <CartesianGrid strokeDasharray="3 3" stroke={c.gridLine} vertical={false} />
                        <XAxis dataKey="note" tick={{ fontSize: 12, fill: c.textDim }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: c.textDim }} axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }} />
                        <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                          {distributionData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {CATEGORIES_CFG.map(cat => (
                    <div key={cat.key} className="av-card" style={{ padding: '22px 24px', background: c.card, border: `1px solid ${c.cardB}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, paddingBottom: 12, borderBottom: `1px solid ${c.divider}` }}>
                        <div style={{ width: 32, height: 32, borderRadius: 9, background: `${cat.color}15`, color: cat.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>{cat.icon}</div>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: c.textDim }}>{cat.label}</div>
                          <div style={{ fontSize: 19, fontWeight: 800, color: noteColor(moyennes[cat.key]) }}>{moyennes[cat.key].toFixed(2)}/5</div>
                        </div>
                        <div style={{ marginLeft: 'auto' }}><StarRating value={moyennes[cat.key]} /></div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {CRITERES[cat.key].map(({ key, label }) => {
                          const m = criteresMoyennes[`${cat.key}.${key}`] || 0;
                          return (
                            <div key={key}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                                <span style={{ fontSize: 12.5, color: c.text, fontWeight: 500 }}>{label}</span>
                              </div>
                              <NoteBar value={m} color={cat.color} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  <div className="av-card" style={{ padding: '22px 24px', gridColumn: '1/-1', background: c.card, border: `1px solid ${c.cardB}` }}>
                    <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: c.textDim, marginBottom: 18, paddingBottom: 12, borderBottom: `1px solid ${c.divider}` }}>
                      Commentaires & Suggestions
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      {avis.filter(a => a.commentaire || a.points_forts || a.points_faibles).map((a, idx) => (
                        <div key={a.id ?? idx} style={{ padding: '14px 16px', background: c.surface, borderRadius: 11, border: `1px solid ${c.cardB}` }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: c.textDim, marginBottom: 8 }}>Participant #{idx + 1}</div>
                          {a.commentaire && (
                            <p style={{ fontSize: 13, color: c.text, lineHeight: 1.7, marginBottom: 8, fontStyle: 'italic' }}>
                              « {a.commentaire} »
                            </p>
                          )}
                          {a.points_forts && (
                            <div style={{ fontSize: 12.5, color: '#198754', display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: 4 }}>
                              <FaCheck style={{ flexShrink: 0, marginTop: 2 }} />
                              <span>{a.points_forts}</span>
                            </div>
                          )}
                          {a.points_faibles && (
                            <div style={{ fontSize: 12.5, color: '#fd7e14', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                              <FaInfoCircle style={{ flexShrink: 0, marginTop: 2 }} />
                              <span>{a.points_faibles}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              )}

              {/* ── DÉTAIL PAR PERSONNE ── */}
              {activeTab === 'detail' && (
                <div className="av-card" style={{ overflow: 'hidden', background: c.card, border: `1px solid ${c.cardB}` }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="av-tbl">
                      <thead>
                        <tr style={{ background: c.theadBg }}>
                          {['#', 'Soumis le', 'Formation', 'Formateur', 'Impact', 'Logistique', 'Moy. globale', 'Détails'].map((h, i) => (
                            <th key={i} style={{ color: c.theadText, whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {avis.map((a, idx) => {
                          const moyF  = avg(Object.values(a.formation  || {}));
                          const moyFo = avg(Object.values(a.formateur  || {}));
                          const moyI  = avg(Object.values(a.impact     || {}));
                          const moyL  = avg(Object.values(a.logistique || {}));
                          const moyG  = avg([moyF, moyFo, moyI, moyL]);
                          return (
                            <tr key={a.id ?? idx} className="av-trow" style={{ cursor: 'pointer' }} onClick={() => setDetailAvis(a)}>
                              <td style={{ borderBottomColor: c.rowBorder }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: c.title }}>#{idx + 1}</div>
                              </td>
                              <td style={{ borderBottomColor: c.rowBorder }}>
                                <div style={{ fontSize: 12.5, color: c.text }}>{new Date(a.soumis_le).toLocaleDateString('fr-FR')}</div>
                                <div style={{ fontSize: 11.5, color: c.textDim }}>{new Date(a.soumis_le).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                              </td>
                              {[moyF, moyFo, moyI, moyL].map((m, i) => (
                                <td key={i} style={{ borderBottomColor: c.rowBorder }}>
                                  <div className="av-npill" style={{ background: `${noteColor(m)}15`, color: noteColor(m) }}>{m.toFixed(1)}</div>
                                </td>
                              ))}
                              <td style={{ borderBottomColor: c.rowBorder }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <div style={{ width: 48, height: 6, borderRadius: 3, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                                    <div style={{ width: `${moyG / 5 * 100}%`, height: '100%', borderRadius: 3, background: noteColor(moyG) }} />
                                  </div>
                                  <span style={{ fontSize: 13, fontWeight: 700, color: noteColor(moyG) }}>{moyG.toFixed(1)}</span>
                                </div>
                              </td>
                              <td style={{ borderBottomColor: c.rowBorder }}>
                                <button
                                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'rgba(25,135,84,0.08)', color: '#198754', border: '1px solid rgba(25,135,84,0.2)', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
                                  onClick={e => { e.stopPropagation(); setDetailAvis(a); }}>
                                  <FaEye style={{ fontSize: 11 }} /> Voir
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── Aucun avis ── */}
              {!loadingDetail && avis.length === 0 && (
                <div className="av-card" style={{ padding: '60px 20px', textAlign: 'center', background: c.card, border: `1px solid ${c.cardB}` }}>
                  <FaQrcode style={{ fontSize: 48, color: c.textDim, display: 'block', margin: '0 auto 16px', opacity: 0.3 }} />
                  <div style={{ fontSize: 16, fontWeight: 700, color: c.title, marginBottom: 6 }}>Aucun avis reçu pour cette session</div>
                  <div style={{ fontSize: 13, color: c.textDim }}>Les données s'afficheront ici dès que les participants auront scanné le QR code et soumis leur évaluation.</div>
                </div>
              )}
            </>
          )}

        </div>
      </main>

      {/* ── MODAL DÉTAIL AVIS ── */}
      {detailAvis && (
        <div className="av-overlay" style={{ background: c.overlay }} onClick={() => setDetailAvis(null)}>
          <div className="av-modal av-card" style={{ background: c.modalBg, border: `1px solid ${c.cardB}` }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '18px 22px', borderBottom: `1px solid ${c.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, background: isDark ? 'rgba(255,255,255,0.03)' : '#fafbfc' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: c.title }}>Détail de l'avis</div>
                <div style={{ fontSize: 12, color: c.textDim }}>Soumis le {new Date(detailAvis.soumis_le).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
              </div>
              <button onClick={() => setDetailAvis(null)} style={{ width: 34, height: 34, borderRadius: 8, border: `1.5px solid ${c.cardB}`, background: 'transparent', color: c.textDim, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                <FaTimes />
              </button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '22px 24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 22 }}>
                {CATEGORIES_CFG.map(cat => {
                  const m = avg(Object.values(detailAvis[cat.key] || {}));
                  return (
                    <div key={cat.key} style={{ padding: '12px 14px', background: c.surface, borderRadius: 11, border: `1px solid ${c.cardB}`, textAlign: 'center' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textDim, marginBottom: 6 }}>{cat.label}</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: noteColor(m) }}>{m.toFixed(1)}</div>
                      <StarRating value={m} size={10} />
                    </div>
                  );
                })}
              </div>
              {CATEGORIES_CFG.map(cat => (
                <div key={cat.key} style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: cat.color, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 7 }}>
                    {cat.icon} {cat.label}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                    {CRITERES[cat.key].map(({ key, label }) => {
                      const n = (detailAvis[cat.key] || {})[key] || 0;
                      return (
                        <div key={key} style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 13, color: c.text, marginBottom: 4 }}>{label}</div>
                            <NoteBar value={n} color={cat.color} />
                          </div>
                          <div className="av-npill" style={{ background: `${noteColor(n)}15`, color: noteColor(n) }}>{n}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              {(detailAvis.commentaire || detailAvis.points_forts || detailAvis.points_faibles) && (
                <div style={{ padding: '14px 16px', background: c.surface, borderRadius: 11, border: `1px solid ${c.cardB}`, marginTop: 4 }}>
                  {detailAvis.commentaire && (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: c.textDim, marginBottom: 7 }}>Commentaire</div>
                      <p style={{ fontSize: 13.5, color: c.text, lineHeight: 1.75, fontStyle: 'italic', marginBottom: 14 }}>« {detailAvis.commentaire} »</p>
                    </>
                  )}
                  {detailAvis.points_forts && (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#198754', marginBottom: 7 }}>Points forts</div>
                      <p style={{ fontSize: 13.5, color: c.text, lineHeight: 1.7, marginBottom: 14 }}>{detailAvis.points_forts}</p>
                    </>
                  )}
                  {detailAvis.points_faibles && (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#fd7e14', marginBottom: 7 }}>Points à améliorer</div>
                      <p style={{ fontSize: 13.5, color: c.text, lineHeight: 1.7 }}>{detailAvis.points_faibles}</p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── PANNEAU QR CODE ── */}
      {showQRPanel && formation && (
        <Qrcodepanel
          formation={{ ...formation, ...sessionDetail }}
          onClose={() => setShowQRPanel(false)}
          onToggleQR={handleToggleQR}
          isDark={isDark}
          c={c}
        />
      )}

      {/* ── MODAL CRÉATION SESSION ── */}
      {showCreateModal && (
        <CreateSessionModalAvis
          onClose={() => setShowCreateModal(false)}
          onCreate={handleSessionCreated}
          isDark={isDark}
          c={c}
        />
      )}

    </div>
  );
};

export default AvisAChaud;